// Stable, machine-readable codes for flow-extension runtime errors. These survive the worker → server IPC
// boundary (as the error `code`), so the console and client can resolve/branch on the error type rather
// than string-matching messages. Do not renumber/rename existing codes — they are part of the contract.
export const FlowExtensionErrorCodes = {
  // A method name that isn't a registered action/dictionary/trigger/SYSTEM method.
  UNKNOWN_METHOD: 'FR_EXT_UNKNOWN_METHOD',
  // A required named param (invocation / callbackObject / refreshToken) was absent from the method input.
  MISSING_METHOD_PARAM: 'FR_EXT_MISSING_METHOD_PARAM',
  // The method input failed its schema (action params, helper payload, SYSTEM invocation).
  INVALID_METHOD_INPUT: 'FR_EXT_INVALID_METHOD_INPUT',
  // A dictionary/loader `criteria` failed the declared criteria schema — reported apart from the payload so
  // the UI can point at the dependent fields instead of the whole call.
  INVALID_CRITERIA: 'FR_EXT_INVALID_CRITERIA',
  // Two registered items (action/dictionary/trigger) share an id.
  DUPLICATE_ITEM_ID: 'FR_EXT_DUPLICATE_ITEM_ID',
  // No v2 extension registered for the given serviceId after requiring the module.
  EXTENSION_NOT_REGISTERED: 'FR_EXT_NOT_REGISTERED',
  // A declared config value is missing or the wrong shape (see v2/schema/normalize).
  INVALID_CONFIG: 'FR_EXT_INVALID_CONFIG',
  // A schema slot that must be a z.object was declared as something else (see v2/schema/assert-object-schema).
  INVALID_SCHEMA: 'FR_EXT_INVALID_SCHEMA',
  // A polling trigger declared zero or multiple mechanisms, or is missing its fetch.
  INVALID_TRIGGER_CONFIG: 'FR_EXT_INVALID_TRIGGER_CONFIG',
  // A trigger dispatch referenced an unregistered trigger id.
  UNKNOWN_TRIGGER: 'FR_EXT_UNKNOWN_TRIGGER',
  // setupRealtimeTriggers is misconfigured (missing subscribe/unsubscribe, or declared twice).
  REALTIME_SETUP: 'FR_EXT_REALTIME_SETUP',
  // Inbound webhook failed signature verification.
  // setupOauth2 is misconfigured (e.g. declared twice).
  OAUTH_SETUP: 'FR_EXT_OAUTH_SETUP',
  // A handler accessed an opt-in feature (oauth/files) that the extension didn't enable.
  FEATURE_NOT_ENABLED: 'FR_EXT_FEATURE_NOT_ENABLED',
  // ...
  INVALID_DICTIONARY_OUTPUT_SCHEME: 'INVALID_DICTIONARY_OUTPUT_SCHEME',
} as const

export type FlowExtensionErrorCode = (typeof FlowExtensionErrorCodes)[keyof typeof FlowExtensionErrorCodes]
